/**
 * The Queue tab — the redesigned triage surface (split-pane).
 *
 * Left: an urgency-ordered list of CASE CARDS in three sections — Needs
 * you / Waiting / Snoozed (collapsed). Every case is a card headed by
 * its identity (codename, client, owner) with the outstanding actions
 * listed inside — one action or five, the shape is the same, so a case
 * never "collapses" into a bare row when it is down to a single item.
 * Right: the preview pane with decision context + the triage verbs
 * (see QueuePreview).
 *
 * Court is per-ITEM here (the old matter-level promotion is gone): an
 * approval needing you shows under Needs you even while a sibling
 * report waits on the client — the same case then appears as a card in
 * each section, each listing only that section's actions.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ActionIcon,
  Box,
  Center,
  Group,
  Loader,
  Pagination,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  IconBellOff,
  IconChevronDown,
  IconChevronRight,
  IconSearch,
  IconX,
} from '@tabler/icons-react';

import { useSearchDraft } from '@/hooks';
import { InboxGroup, InboxGroupedParams, InboxItem } from '@/types/inbox';
import { CaseCard } from './CaseCard';
import { deepLinkForInboxItem } from './deepLink';
import { QueuePreview } from './QueuePreview';
import { RowOwnerContext } from './QueueRow';
import { formatWakeAt } from './timeAgo';

interface QueueTabProps {
  groups: InboxGroup[];
  snoozedItems: InboxItem[];
  groupTotal: number;
  page: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  error: Error | null;
  filters: InboxGroupedParams;
  onFiltersChange: (next: InboxGroupedParams) => void;
  availableKinds: string[];
  currentUserId: string | null;
  selectedId: string | null;
  /** Explicit = a real user click (marks the item seen); implicit =
   * the default-selection effect (must NOT clear the new-dot). */
  onSelectItem: (item: InboxItem, opts: { explicit: boolean }) => void;
  /** Ids the user has viewed this session (optimistic seen-state). */
  locallySeen: ReadonlySet<string>;
  onClaim: (familyKey: string, userId: string | null) => Promise<void>;
  /** Per-item assignee override (POST /inbox/{id}/assign). */
  onItemAssign: (item: InboxItem, userId: string | null) => Promise<void>;
  /** Fired after a codename rename so the queue regroups. */
  onCodenameRenamed?: () => void | Promise<void>;
  onRequestSnooze: (item: InboxItem) => void;
  onRequestDismiss: (item: InboxItem) => void;
  onRequestResolve: (item: InboxItem) => void;
  /** One of a case's asks was answered in the preview. */
  onAnswered?: () => void | Promise<void>;
  /** The caller's own needs-you count, for the Mine control. */
  mineCount?: number | null;
}

interface FamilyBlock {
  group: InboxGroup;
  items: InboxItem[];
}

const FOREVER = Number.POSITIVE_INFINITY;

function urgency(item: InboxItem): number {
  return item.dueAt ? new Date(item.dueAt).getTime() : FOREVER;
}

function activity(item: InboxItem): number {
  return new Date(item.updatedAt ?? item.createdAt).getTime();
}

/** Family blocks for one court, most-urgent first (soonest due, then
 * newest activity); same ordering within a block. */
export function familyBlocks(groups: InboxGroup[], court: 'needs_you' | 'waiting'): FamilyBlock[] {
  const blocks: FamilyBlock[] = [];
  for (const group of groups) {
    const items = group.items
      .filter((i) => i.state === 'open' && i.court === court)
      .sort((a, b) => urgency(a) - urgency(b) || activity(b) - activity(a));
    if (items.length > 0) {
      blocks.push({ group, items });
    }
  }
  return blocks.sort((a, b) => {
    const dueDiff = urgency(a.items[0]) - urgency(b.items[0]);
    if (dueDiff !== 0) {
      return dueDiff;
    }
    return activity(b.items[0]) - activity(a.items[0]);
  });
}

function SectionHeader({
  label,
  count,
  hint,
}: Readonly<{ label: string; count: number; hint?: string }>) {
  return (
    <Group gap={8} px="sm" pt="md" pb={4}>
      <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.08em' }}>
        {label}
      </Text>
      <Text size="xs" c="dimmed">
        {count}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed" ml="auto">
          {hint}
        </Text>
      )}
    </Group>
  );
}

/** One family's card for a court section. */
function FamilyCard({
  block,
  selectedId,
  locallySeen,
  currentUserId,
  onClaim,
  onSelect,
}: Readonly<{
  block: FamilyBlock;
  selectedId: string | null;
  locallySeen: ReadonlySet<string>;
  currentUserId: string | null;
  onClaim: (familyKey: string, userId: string | null) => Promise<void>;
  onSelect: (item: InboxItem) => void;
}>) {
  const { group, items } = block;
  const isNew = (item: InboxItem) => item.lastSeenAt === null && !locallySeen.has(item.id);
  // The header avatar claims/assigns the CASE — only meaningful when
  // the family has a claimable root.
  const claimable = group.summary != null && group.summary.rootEntityType !== 'report';
  const owner: RowOwnerContext | null = claimable
    ? {
        familyKey: group.key,
        caseAnalyst: group.analyst,
        currentUserId,
        onClaim,
      }
    : null;

  return (
    <CaseCard
      identity={{
        codename: group.key,
        clientName: group.client?.name ?? null,
        clientCode: group.client?.clientCode ?? null,
        expedited: group.summary?.expedited ?? false,
        subjectNames: group.summary?.subjectNames ?? [],
        subjectTotal: group.summary?.subjectTotal ?? 0,
      }}
      analyst={group.analyst}
      owner={owner}
      items={items}
      selectedId={selectedId}
      isNew={isNew}
      onSelect={onSelect}
    />
  );
}

/** One court's section: header (items · cases) + a card per family.
 * Renders nothing when the court is empty. */
function CourtSection({
  court,
  label,
  blocks,
  selectedId,
  locallySeen,
  currentUserId,
  onClaim,
  onSelect,
}: Readonly<{
  court: 'needs_you' | 'waiting';
  label: string;
  blocks: FamilyBlock[];
  selectedId: string | null;
  locallySeen: ReadonlySet<string>;
  currentUserId: string | null;
  onClaim: (familyKey: string, userId: string | null) => Promise<void>;
  onSelect: (item: InboxItem) => void;
}>) {
  const count = blocks.reduce((n, b) => n + b.items.length, 0);
  if (count === 0) {
    return null;
  }
  return (
    <Stack gap={0} data-testid={`court-${court}`}>
      <SectionHeader
        label={label}
        count={count}
        hint={`${blocks.length} ${blocks.length === 1 ? 'case' : 'cases'}`}
      />
      {blocks.map((block) => (
        <FamilyCard
          key={block.group.key}
          block={block}
          selectedId={selectedId}
          locallySeen={locallySeen}
          currentUserId={currentUserId}
          onClaim={onClaim}
          onSelect={onSelect}
        />
      ))}
    </Stack>
  );
}

export function QueueTab({
  groups,
  snoozedItems,
  groupTotal,
  page,
  onPageChange,
  isLoading,
  error,
  filters,
  onFiltersChange,
  availableKinds,
  currentUserId,
  selectedId,
  onSelectItem,
  locallySeen,
  onClaim,
  onItemAssign,
  onCodenameRenamed,
  onRequestSnooze,
  onRequestDismiss,
  onRequestResolve,
  onAnswered,
  mineCount = null,
}: Readonly<QueueTabProps>) {
  const navigate = useNavigate();
  // "Mine" is a server-side filter (the item's assignee, else the case
  // owner), so the page and its counts are the caller's own.
  const scope = filters.mine ? 'mine' : 'everyone';
  const [snoozedOpen, setSnoozedOpen] = useState(false);
  const pageCount = Math.ceil(groupTotal / (filters.pageSize ?? 20));

  const [searchDraft, setSearchDraft] = useSearchDraft(filters.search ?? '', (settled) =>
    onFiltersChange({ ...filters, search: settled || undefined })
  );

  const needsYou = useMemo(() => familyBlocks(groups, 'needs_you'), [groups]);
  const waiting = useMemo(() => familyBlocks(groups, 'waiting'), [groups]);
  const needsYouCount = needsYou.reduce((n, b) => n + b.items.length, 0);
  const waitingCount = waiting.reduce((n, b) => n + b.items.length, 0);

  const groupByItemId = useMemo(() => {
    const map = new Map<string, InboxGroup>();
    for (const g of groups) {
      for (const i of g.items) {
        map.set(i.id, g);
      }
    }
    return map;
  }, [groups]);

  const allVisible = useMemo(
    () => [
      ...needsYou.flatMap((b) => b.items),
      ...waiting.flatMap((b) => b.items),
      ...snoozedItems,
    ],
    [needsYou, waiting, snoozedItems]
  );
  const selectedItem = allVisible.find((i) => i.id === selectedId) ?? null;

  // Default selection: first needs-you row (implicit — keeps new-dots).
  useEffect(() => {
    if (!selectedItem && allVisible.length > 0) {
      onSelectItem(allVisible[0], { explicit: false });
    }
    // onSelectItem churns identity per render; keying on the data is enough.
  }, [selectedItem, allVisible]);

  const handleSelect = (item: InboxItem) => {
    // No room for the pane on narrow screens — go straight to the case.
    const narrow = window.matchMedia?.('(max-width: 62em)')?.matches ?? false;
    if (narrow) {
      const link = deepLinkForInboxItem(item);
      if (link) {
        navigate(link.path);
        return;
      }
    }
    onSelectItem(item, { explicit: true });
  };

  const nextWake = snoozedItems
    .map((i) => i.wakeAt)
    .filter((w): w is string => w !== null)
    .sort((a, b) => a.localeCompare(b))[0];

  return (
    <Stack gap="sm" data-testid="queue-tab">
      <Group gap="sm" wrap="wrap" align="center">
        <TextInput
          placeholder="Search codename, client, subject…"
          leftSection={<IconSearch size={14} />}
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.currentTarget.value)}
          rightSection={
            searchDraft ? (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => setSearchDraft('')}
                aria-label="Clear search"
              >
                <IconX size={14} />
              </ActionIcon>
            ) : undefined
          }
          w={260}
          data-testid="queue-search"
        />
        <Select
          placeholder="Kind"
          clearable
          searchable
          data={availableKinds}
          value={filters.kind ?? null}
          onChange={(v) => onFiltersChange({ ...filters, kind: v ?? undefined })}
          w={220}
          data-testid="queue-kind"
        />
        <SegmentedControl
          size="xs"
          value={scope}
          onChange={(v) => onFiltersChange({ ...filters, mine: v === 'mine' || undefined })}
          data={[
            { label: 'Everyone', value: 'everyone' },
            {
              label: mineCount !== null ? `Mine · ${mineCount}` : 'Mine',
              value: 'mine',
              disabled: !currentUserId,
            },
          ]}
          data-testid="queue-scope"
        />
        <Text size="sm" c="dimmed" ml="auto">
          {needsYouCount} need you · {waitingCount} waiting · {snoozedItems.length} snoozed
        </Text>
      </Group>

      {error && (
        <Text c="red" size="sm">
          Failed to load inbox: {error.message}
        </Text>
      )}

      {isLoading && groups.length === 0 ? (
        <Center mih={200}>
          <Loader size="md" />
        </Center>
      ) : (
        <Group align="flex-start" gap="md" wrap="nowrap">
          <Stack gap={0} style={{ flex: 1.2, minWidth: 0 }} data-testid="queue-list">
            {needsYouCount === 0 && waitingCount === 0 && snoozedItems.length === 0 ? (
              <Center mih={200}>
                <Stack align="center" gap="xs">
                  <IconBellOff size={40} stroke={1.4} color="var(--mantine-color-gray-5)" />
                  <Text c="dimmed">Queue's clear.</Text>
                </Stack>
              </Center>
            ) : (
              <>
                <CourtSection
                  court="needs_you"
                  label="Needs you"
                  blocks={needsYou}
                  selectedId={selectedId}
                  locallySeen={locallySeen}
                  currentUserId={currentUserId}
                  onClaim={onClaim}
                  onSelect={handleSelect}
                />
                <CourtSection
                  court="waiting"
                  label="Waiting"
                  blocks={waiting}
                  selectedId={selectedId}
                  locallySeen={locallySeen}
                  currentUserId={currentUserId}
                  onClaim={onClaim}
                  onSelect={handleSelect}
                />
                {snoozedItems.length > 0 && (
                  <Stack gap={0} data-testid="queue-snoozed">
                    <Group gap={8} px="sm" pt="md" pb={4}>
                      <UnstyledButton
                        onClick={() => setSnoozedOpen((v) => !v)}
                        aria-expanded={snoozedOpen}
                        data-testid="snoozed-toggle"
                      >
                        <Group gap={6}>
                          {snoozedOpen ? (
                            <IconChevronDown size={12} />
                          ) : (
                            <IconChevronRight size={12} />
                          )}
                          <Text
                            size="xs"
                            fw={700}
                            tt="uppercase"
                            c="dimmed"
                            style={{ letterSpacing: '0.08em' }}
                          >
                            Snoozed
                          </Text>
                          <Text size="xs" c="dimmed">
                            {snoozedItems.length}
                          </Text>
                        </Group>
                      </UnstyledButton>
                      {!snoozedOpen && nextWake && (
                        <Text size="xs" c="dimmed" ml="auto">
                          next wakes {formatWakeAt(nextWake)}
                        </Text>
                      )}
                    </Group>
                    {snoozedOpen &&
                      snoozedItems.map((item) => (
                        // Snoozed rows come from the flat endpoint without
                        // group context — a one-action card per item,
                        // identified by its cached codename.
                        <CaseCard
                          key={item.id}
                          identity={{
                            codename: item.codenameFamilyCached ?? item.codenameCached ?? '—',
                            clientName: null,
                          }}
                          analyst={null}
                          owner={null}
                          items={[item]}
                          selectedId={selectedId}
                          isNew={() => false}
                          onSelect={handleSelect}
                        />
                      ))}
                  </Stack>
                )}
                {pageCount > 1 && (
                  <Group justify="center" pt="md">
                    <Pagination
                      value={page}
                      onChange={onPageChange}
                      total={pageCount}
                      size="sm"
                      data-testid="queue-pagination"
                    />
                  </Group>
                )}
              </>
            )}
          </Stack>

          <Box
            visibleFrom="md"
            style={{
              flex: 1,
              minWidth: 0,
              position: 'sticky',
              top: 'calc(var(--app-shell-header-height, 0px) + var(--mantine-spacing-md))',
              alignSelf: 'flex-start',
              maxHeight:
                'calc(100vh - var(--app-shell-header-height, 0px) - 2 * var(--mantine-spacing-md))',
              overflowY: 'auto',
            }}
            data-testid="queue-pane"
          >
            {selectedItem ? (
              <QueuePreview
                item={selectedItem}
                group={groupByItemId.get(selectedItem.id) ?? null}
                currentUserId={currentUserId}
                onClaim={onClaim}
                onItemAssign={onItemAssign}
                onCodenameRenamed={onCodenameRenamed}
                onRequestSnooze={onRequestSnooze}
                onRequestDismiss={onRequestDismiss}
                onRequestResolve={onRequestResolve}
                onAnswered={onAnswered}
              />
            ) : (
              <Center mih={120}>
                <Text size="sm" c="dimmed">
                  Select an item to preview it here.
                </Text>
              </Center>
            )}
          </Box>
        </Group>
      )}
    </Stack>
  );
}
